import { useState, useEffect, useCallback } from "react";

/**
 * 画像URLからHTMLImageElementを非同期でロードするカスタムフック
 * タブ切り替え時の再読み込みにも対応
 * @param url - ロードする画像のURL（undefined可）
 * @returns ロード済みのHTMLImageElement、またはnull
 */
export const useLoadImage = (url: string | undefined): HTMLImageElement | null => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  // 画像をロードする関数
  const loadImage = useCallback(() => {
    if (!url) {
      setImage(null);
      return;
    }

    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.src = url;
    img.onload = () => setImage(img);
    img.onerror = () => setImage(null);

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [url]);

  // URLが変更された時に画像をロード
  useEffect(() => {
    const cleanup = loadImage();
    return cleanup;
  }, [loadImage]);

  // タブがアクティブになった時に画像を再ロード
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && url) {
        // タブがアクティブになった時、画像が無効になっている可能性があるので再ロード
        loadImage();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [url, loadImage]);

  return image;
};

export default useLoadImage;
